import { FC, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import Button from "../../../components/Button";
import ModalForm, { Field, FieldType } from "../../../components/ModalForm";
import Text from "../../../components/Text";
import {
    bulkUpdateUsers,
    resolveUsersByEmails,
} from "../../../services/api/admin/bulkUsers";
import { getAllRoles } from "../../../services/api/admin/role";
import { getAllTags } from "../../../services/api/admin/tags";
import { BulkUserAction } from "../../../types/api/admin/bulkUsers";
import { Tag } from "../../../types/api/admin/tag";
import { Role } from "../../../types/Role";
import {
    extractEmailsFromCsvColumn,
    guessEmailColumn,
    parseCsv,
} from "../../../utils/csv";
import { CSV_FILE_ACCEPT } from "../../../utils/fileAccept";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    notifyFieldErrors,
    setFieldErrors,
} from "../../../utils/errors";
import styles from "./styles.module.scss";

type Step = "file" | "action";

type Props = {
    open: boolean;
    handleClose: () => void;
    onCompleted?: () => void;
};

const ACTION_OPTIONS: { value: BulkUserAction; label: string }[] = [
    { value: "change_role", label: "Changer le rôle" },
    { value: "add_tag", label: "Ajouter un tag" },
    { value: "remove_tag", label: "Retirer un tag" },
    { value: "deactivate", label: "Désactiver le compte" },
];

const downloadIgnoredEmailsCsv = (emails: string[]) => {
    const csv = "\uFEFFemail\n" + emails.map((email) => `"${email.replace(/"/g, '""')}"`).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
        "download",
        `emails_ignores_${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const ImportBulkModal: FC<Props> = ({ open, handleClose, onCompleted }) => {
    const [step, setStep] = useState<Step>("file");
    const [file, setFile] = useState<File | undefined>();
    const [headers, setHeaders] = useState<string[]>([]);
    const [rows, setRows] = useState<string[][]>([]);
    const [emailColumn, setEmailColumn] = useState("");
    const [emails, setEmails] = useState<string[]>([]);
    const [matchedCount, setMatchedCount] = useState(0);
    const [ignoredEmails, setIgnoredEmails] = useState<string[]>([]);
    const [action, setAction] = useState<BulkUserAction>("change_role");
    const [roleId, setRoleId] = useState("");
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [fieldErrors, setFieldErrorsState] = useState<FieldErrors>({});

    const ignoredCount = ignoredEmails.length;

    const resetState = () => {
        setStep("file");
        setFile(undefined);
        setHeaders([]);
        setRows([]);
        setEmailColumn("");
        setEmails([]);
        setMatchedCount(0);
        setIgnoredEmails([]);
        setAction("change_role");
        setRoleId("");
        setTagIds([]);
        setFieldErrorsState({});
    };

    const closeAndReset = () => {
        resetState();
        handleClose();
    };

    useEffect(() => {
        if (!open) return;
        setLoadingMeta(true);
        Promise.all([getAllRoles(), getAllTags()])
            .then(([loadedRoles, loadedTags]) => {
                setRoles(loadedRoles ?? []);
                setTags(loadedTags ?? []);
            })
            .catch(() => {
                toast.error("Erreur lors du chargement des rôles/tags");
            })
            .finally(() => setLoadingMeta(false));
    }, [open]);

    const emailPreviewCount = useMemo(() => {
        if (!emailColumn || rows.length === 0) return 0;
        return extractEmailsFromCsvColumn(headers, rows, emailColumn).length;
    }, [emailColumn, headers, rows]);

    const handleFileChange = async (nextFile: File | undefined) => {
        setFile(nextFile);
        setFieldErrorsState((prev) => clearFieldError(prev, "csv"));
        setHeaders([]);
        setRows([]);
        setEmailColumn("");
        setIgnoredEmails([]);
        if (!nextFile) return;

        try {
            const text = await nextFile.text();
            const parsed = parseCsv(text);
            if (parsed.headers.length === 0) {
                toast.error("Le fichier CSV est vide ou invalide");
                return;
            }
            setHeaders(parsed.headers);
            setRows(parsed.rows);
            setEmailColumn(guessEmailColumn(parsed.headers));
        } catch {
            toast.error("Impossible de lire le fichier CSV");
        }
    };

    const handleDownloadIgnored = () => {
        if (ignoredEmails.length === 0) {
            toast.info("Aucun email ignoré à exporter");
            return;
        }
        downloadIgnoredEmailsCsv(ignoredEmails);
    };

    const handleResolve = async () => {
        const nextErrors = setFieldErrors(
            ["csv", !file && REQUIRED_FIELD],
            ["emailColumn", !!file && !emailColumn && REQUIRED_FIELD]
        );
        setFieldErrorsState(nextErrors);
        if (
            notifyFieldErrors(nextErrors, {
                message: "Veuillez choisir un fichier et une colonne email",
                type: "error",
            })
        ) {
            return;
        }
        const extracted = extractEmailsFromCsvColumn(
            headers,
            rows,
            emailColumn
        );
        if (extracted.length === 0) {
            toast.error("Aucun email trouvé dans la colonne sélectionnée");
            return;
        }

        const result = await resolveUsersByEmails(extracted);
        if (!result) {
            toast.error("Erreur lors du matching des emails");
            return;
        }

        setEmails(extracted);
        setMatchedCount(result.matched.length);
        setIgnoredEmails(result.ignoredEmails ?? []);

        if (result.matched.length === 0) {
            toast.warning(
                `Aucun utilisateur trouvé (${result.ignoredCount} adresse(s) ignorée(s))`
            );
            return;
        }

        setStep("action");
    };

    const handleApply = async () => {
        if (matchedCount === 0) {
            toast.error("Aucun utilisateur à mettre à jour");
            return;
        }

        const nextErrors = setFieldErrors(
            [
                "roleId",
                action === "change_role" && !roleId && REQUIRED_FIELD,
            ],
            [
                "tagIds",
                (action === "add_tag" || action === "remove_tag") &&
                    tagIds.length === 0 &&
                    REQUIRED_FIELD,
            ]
        );
        setFieldErrorsState(nextErrors);
        const bulkToastMessage = nextErrors.roleId
            ? "Veuillez sélectionner un rôle"
            : nextErrors.tagIds
              ? "Veuillez sélectionner au moins un tag"
              : undefined;
        if (
            notifyFieldErrors(nextErrors, {
                message: bulkToastMessage,
                type: "error",
            })
        ) {
            return;
        }

        const result = await bulkUpdateUsers({
            emails,
            action,
            roleId: action === "change_role" ? roleId : undefined,
            tagIds:
                action === "add_tag" || action === "remove_tag"
                    ? tagIds
                    : undefined,
        });

        if (!result) {
            toast.error("Erreur lors de l'action groupée");
            return;
        }

        if (result.ignoredEmails?.length) {
            setIgnoredEmails(result.ignoredEmails);
        }

        toast.success(
            `${result.updatedCount} utilisateur(s) mis à jour (${result.ignoredCount} ignoré(s))`
        );
        onCompleted?.();
        closeAndReset();
    };

    const ignoredExportBlock =
        ignoredCount > 0 ? (
            <div className={styles.ignored_export}>
                <Text variant="body" className={styles.preview}>
                    {ignoredCount} adresse
                    {ignoredCount > 1 ? "s" : ""} ignorée
                    {ignoredCount > 1 ? "s" : ""} (introuvable
                    {ignoredCount > 1 ? "s" : ""} en base)
                </Text>
                <Button
                    size="small"
                    variant="outlined"
                    label="Télécharger les emails ignorés (CSV)"
                    onClick={handleDownloadIgnored}
                />
            </div>
        ) : null;

    const fileFields: Field<FieldType>[] = [
        {
            id: "csv",
            label: "Fichier CSV",
            type: "file",
            accept: CSV_FILE_ACCEPT,
            value: file,
            onChange: handleFileChange,
            error: fieldErrors.csv,
        },
        ...(headers.length > 0
            ? ([
                  {
                      id: "emailColumn",
                      label: "Colonne email",
                      type: "select",
                      value: emailColumn,
                      onChange: (value: string) => {
                          setEmailColumn(value);
                          setFieldErrorsState((prev) =>
                              clearFieldError(prev, "emailColumn")
                          );
                      },
                      error: fieldErrors.emailColumn,
                      options: headers.map((header) => ({
                          value: header,
                          label: header,
                      })),
                  },
              ] as Field<FieldType>[])
            : []),
    ];

    const actionFields: Field<FieldType>[] = [
        {
            id: "action",
            label: "Action",
            type: "select",
            value: action,
            onChange: (value: string) => {
                setAction(value as BulkUserAction);
                setRoleId("");
                setTagIds([]);
                setFieldErrorsState((prev) => {
                    let next = clearFieldError(prev, "roleId");
                    next = clearFieldError(next, "tagIds");
                    return next;
                });
            },
            options: ACTION_OPTIONS,
        },
        ...(action === "change_role"
            ? ([
                  {
                      id: "roleId",
                      label: "Nouveau rôle",
                      type: "select",
                      value: roleId,
                      onChange: (value: string) => {
                          setRoleId(value);
                          setFieldErrorsState((prev) =>
                              clearFieldError(prev, "roleId")
                          );
                      },
                      error: fieldErrors.roleId,
                      options: [
                          { value: "", label: "-" },
                          ...roles.map((role) => ({
                              value: role.id,
                              label: role.name,
                          })),
                      ],
                  },
              ] as Field<FieldType>[])
            : []),
        ...(action === "add_tag" || action === "remove_tag"
            ? ([
                  {
                      id: "tagIds",
                      label: "Tags",
                      type: "select-multiple",
                      value: tagIds,
                      onChange: (value: string[]) => {
                          setTagIds(value);
                          setFieldErrorsState((prev) =>
                              clearFieldError(prev, "tagIds")
                          );
                      },
                      error: fieldErrors.tagIds,
                      options: tags.map((tag) => ({
                          value: tag.id,
                          label: tag.name,
                      })),
                  },
              ] as Field<FieldType>[])
            : []),
    ];

    if (!open) return null;

    if (step === "file") {
        return (
            <ModalForm
                open={open}
                handleClose={closeAndReset}
                title="Import CSV"
                fields={fileFields}
                submitLabel="Continuer"
                onSubmit={handleResolve}
                size="medium"
                isLoading={loadingMeta}
                contentAbove={
                    <>
                        {emailColumn && emailPreviewCount > 0 ? (
                            <Text variant="body" className={styles.preview}>
                                {emailPreviewCount} adresse(s) email unique(s)
                                détectée(s) dans « {emailColumn} »
                            </Text>
                        ) : null}
                        {ignoredExportBlock}
                    </>
                }
            />
        );
    }

    return (
        <ModalForm
            open={open}
            handleClose={closeAndReset}
            title="Action groupée"
            fields={actionFields}
            submitLabel="Appliquer"
            onSubmit={handleApply}
            size="medium"
            contentAbove={
                <>
                    <Text variant="body" className={styles.preview}>
                        {matchedCount} utilisateur
                        {matchedCount > 1 ? "s" : ""} trouvé
                        {matchedCount > 1 ? "s" : ""} avec ces adresses mails
                        {ignoredCount > 0
                            ? ` (${ignoredCount} ignorée${
                                  ignoredCount > 1 ? "s" : ""
                              })`
                            : ""}
                        . Que faire ?
                    </Text>
                    {ignoredExportBlock}
                </>
            }
        />
    );
};

export default ImportBulkModal;
